import React, { useState, useRef } from 'react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'

type Props = {
  listening: boolean
  onMouseDown: () => void
  onMouseUp: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  onKeyUp: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  disabled?: boolean
  ariaLabel?: string
}

export function VoiceInputButton({
  listening,
  onMouseDown,
  onMouseUp,
  onKeyDown,
  onKeyUp,
  disabled = false,
  ariaLabel = 'hold to speak',
}: Props) {
  const [isPressed, setIsPressed] = useState(false)
  const pressTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleStop = () => {
    setIsPressed(false)
    if (pressTimeoutRef.current) {
      clearTimeout(pressTimeoutRef.current)
    }
    onMouseUp()
  }

  const handleStart = () => {
    setIsPressed(true)
    if (pressTimeoutRef.current) {
      clearTimeout(pressTimeoutRef.current)
    }
    onMouseDown()
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    handleStart()
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    handleStop()
  }

  const handlePointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    handleStop()
  }

  const handlePointerLeave = () => {
    if (isPressed) {
      handleStop()
    }
  }

  const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      handleStart()
    }
    onKeyDown(e)
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      handleStop()
    }
    onKeyUp(e)
  }

  return (
    <Tooltip title="Hold to speak">
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={ariaLabel}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerLeave}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onContextMenu={handleContextMenu}
        sx={{
          width: '32px',
          height: '32px',
          borderRadius: '100px',
          padding: '4px',
          color: listening ? 'error.main' : 'action.active',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        {listening ? (
          <MicIcon fontSize="small" />
        ) : (
          <MicOffIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  )
}
